import { useTracking } from '@components/hooks';
import styles from '@components/components/ProductComponents/components/ProductDecider/ProductDecider.module.scss';
import SignUpPlanModal from '@components/components/Modal/SignUpPlanModal/SignUpPlanModal';
import ProductCard from '@components/components/ProductComponents/ProductCard';
import ProductCardLegacy from '@components/components/ProductComponentsLegacy/ProductCard/ProductCard';
import ProductCardPriceLegacy from '@components/components/ProductComponentsLegacy/ProductCardPrice/ProductCardPrice';
import { ProductCardPrices } from '@components/components/ProductComponentsLegacy/ProductCardPrice/ProductCardPrice';
import { useLocalStorage } from '@components/hooks';
import {
  Button,
  Content,
  Link,
  Modal,
  Table,
} from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { useDialogStore } from '@ariakit/react';

/**
 * This component determines which product card to render.
 *  @TODO: Can be removed once all products have been transitioned to the new product cards.
 *
 */
export const ProductDecider = ({
  handleDropdownChange,
  hasPromo,
  isEditMode,
  isGeneralRecommender,
  maxEyecatcherHeight = 0,
  newUrlOptions,
  product,
  productCardsRef,
  productOption,
  registerButton,
}) => {
  const dialog = useDialogStore();
  const orderDialog = useDialogStore();
  const trackEvent = 'tarifCardEvent';
  const initialRender = useRef(true);
  const track = useTracking();
  const [storage] = useLocalStorage('address-check');

  const {
    type,
    content: {
      prices,
      details,
      detailText,
      options,
      mixtype,
      bindung,
      capprice,
      detailLink,
    },
    eyecatcher,
    recommended: recommendedProduct,
  } = product;

  // track events for legacy product cards that use order dialog
  // @TODO: remove after strom, gas, business_strom, business_gas moved to new product cards
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      track({
        event: trackEvent,
        interaction: orderDialog.visible
          ? 'click-tarif-auswaehlen'
          : 'click-modal-schließen',
        tarifId: product.key,
        tarifName: product.title,
        tarifCommitment: bindung === 'year' ? 12 : 0,
        tarifSourceType: mixtype,
        tarifCap: capprice === 'yes',
      });
    }
  }, [orderDialog.visible]);

  // calculate height of eyecatcher for old product cards
  // recommended product is for tanke category only after use of TankeRecommender
  const cardPadding =
    maxEyecatcherHeight -
    [!!eyecatcher?.eyecatcher_valid_from, !!recommendedProduct].filter(Boolean)
      .length;

  switch (type) {
    case 'superschnell':
      return (
        <div
          className={classNames(styles.productCardWrapper, {
            [styles.zeroLineFiller]:
              cardPadding === 1 &&
              !eyecatcher?.eyecatcher_valid_from &&
              !recommendedProduct,
            [styles.oneLineFiller]: cardPadding === 1,
            [styles.twoLineFiller]: cardPadding === 2,
          })}
        >
          <ProductCardLegacy
            className={styles.cardLegacy}
            detailLink={product?.detailLink}
            fixHeight={product.fixHeight}
            hasPromo={hasPromo || product.hasPromo}
            hideDetailLink={product.type === 'tanke'}
            icon={
              product.image && (
                <img alt="" src={product.image} title={product.title} />
              )
            }
            options={product?.content?.options}
            price={
              product?.content?.prices && (
                <ProductCardPrices priceItems={product?.content?.prices} />
              )
            }
            product={product}
            productOption={product.content}
            registerButton={
              <Button
                className={styles.cta}
                isElevated
                render={
                  <Link
                    target={
                      product.cta?.target.length > 0
                        ? product.cta?.target
                        : '_self'
                    }
                    to={
                      ['JA', 'IM_GEBAEUDE'].includes(storage?.Internet) &&
                      product.type === 'superschnell'
                        ? product.new_customer_url + newUrlOptions
                        : product.cta?.url
                    }
                  >
                    {product.cta?.title || 'Bestellen'}
                  </Link>
                }
              />
            }
            to={product.detail_url}
            type={product.type}
          />
        </div>
      );
    case 'tanke':
    case 'business_tanke':
      // use legacy product cards
      // all modals & logic handeled in this file
      return (
        <>
          <SignUpPlanModal
            dialog={orderDialog}
            existingCustomerUrl={product.existing_customer_url}
            id={product.key}
            newCustomerUrl={product.new_customer_url}
            newCustomerUrls={product.new_customer_urls}
            title={product.title}
            trackEvent={trackEvent}
          />
          <Modal header={product.title} store={dialog}>
            <div className={styles.productModal}>
              {!!detailText && <Content noSpacing>{detailText}</Content>}
            </div>
          </Modal>
          <div
            className={classNames(styles.productCardWrapper, {
              [styles.zeroLineFiller]:
                cardPadding === 1 &&
                !eyecatcher?.eyecatcher_valid_from &&
                !recommendedProduct,
              [styles.oneLineFiller]: cardPadding === 1,
              [styles.twoLineFiller]: cardPadding === 2,
            })}
            data-componentname={product.title}
            data-purpose="category-products"
          >
            <ProductCardLegacy
              className={styles.cardLegacy}
              detailLink={detailLink}
              handleDropdownChange={handleDropdownChange}
              icon={
                <img
                  alt={product.title}
                  src={product.image}
                  title={product.title}
                />
              }
              maxEyecatcherHeight={maxEyecatcherHeight}
              okoCTA={product.oko && product.phone && product.show_oko}
              okoShow={product.oko && product.phone && !product.show_oko}
              options={options}
              orderDialog={orderDialog}
              price={
                prices && (
                  <>
                    {prices.map(
                      ({ main_price: mainPrice, unit, subline }, items) => (
                        <ProductCardPriceLegacy
                          additional={subline}
                          key={`${mainPrice}_${unit}`}
                          multiplePrices={items.length > 1}
                          price={mainPrice}
                          tarifweltNeu={product.is_tarifwelt_new}
                          theme={null}
                          unit={unit}
                          withMore={product.calculated ? dialog : null}
                        />
                      ),
                    )}
                  </>
                )
              }
              product={product}
              productOption={productOption}
              registerButton={registerButton || null}
              to={product?.url}
            />
          </div>
        </>
      );
    case 'strom':
    case 'business_strom':
    case 'gas':
    case 'business_gas':
      // use new product cards
      // modals and other logic handeled in useProductCard.js
      return (
        <div
          className={styles.productCardWrapper}
          data-componentname={product.title}
          data-purpose="category-products"
        >
          <ProductCard
            className={styles.card}
            detailLink={detailLink}
            handleDropdownChange={handleDropdownChange}
            isEditMode={isEditMode}
            isGeneralRecommender={isGeneralRecommender}
            options={options}
            product={product}
            productCardsRef={productCardsRef}
            productOption={productOption}
            registerButton={registerButton || null}
          />
        </div>
      );
    default:
      return null;
  }
};

ProductDecider.propTypes = {
  handleDropdownChange: PropTypes.func,
  hasPromo: PropTypes.bool,
  isEditMode: PropTypes.bool,
  maxEyecatcherHeight: PropTypes.number,
  newUrlOptions: PropTypes.string,
  // exact shape of prouducts depends on the product type
  // eslint-disable-next-line react/forbid-prop-types
  product: PropTypes.object.isRequired,
  productCardsRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  productOption: PropTypes.shape({
    key: PropTypes.string,
    mixtype: PropTypes.string,
    bindung: PropTypes.string,
    capprice: PropTypes.string,
  }),
  registerButton: PropTypes.node,
};

export default ProductDecider;
